/**
 * Cases Page - Minimal index to make Cases discoverable (Phase 1)
 */

import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Search, RefreshCw, FileText, ExternalLink } from 'lucide-react';
import { api } from '../lib/api';
import { pageStyles, formStyles, iconSizes, buttonStyles } from '../lib/styles';
import { cn } from '../lib/utils';
import { Card, CardContent, CardHeader } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';

type CaseListItem = {
  case: {
    id: string;
    status: 'OPEN' | 'CLOSED';
    archivedAt: number | null;
    createdAt: number;
    updatedAt: number;
  };
  customer: {
    id: string;
    name: string;
    email: string;
    phone: string;
  };
  vehicle: {
    id: string;
    registration: string;
    make: string;
    model: string;
    year: number | null;
    color: string | null;
  };
};

export default function CasesPage() {
  const [q, setQ] = useState('');

  const queryKey = useMemo(() => ['cases', { q }], [q]);

  const {
    data,
    isLoading,
    isError,
    refetch,
    error,
  } = useQuery({
    queryKey,
    queryFn: async () => {
      const params = new URLSearchParams();
      params.set('status', 'open');
      params.set('limit', '100');
      if (q.trim()) params.set('q', q.trim());

      const res = await api.get<{ items: CaseListItem[]; limit: number; offset: number }>(`/api/cases?${params}`);
      return res.items;
    },
  });

  const formatTs = (ts: number) => new Date(ts * 1000).toLocaleString('es-ES', { dateStyle: 'short', timeStyle: 'short' });

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className={pageStyles.title}>Casos</h1>
          <p className={pageStyles.subtitle}>Vista unificada por cliente + vehículo</p>
        </div>

        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className={formStyles.searchIcon} />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Buscar matrícula o cliente…"
              className={cn(formStyles.inputWithIcon, 'w-full sm:w-64')}
            />
          </div>
          <button
            onClick={() => refetch()}
            disabled={isLoading}
            className={buttonStyles.icon}
            title="Actualizar"
          >
            <RefreshCw className={cn(iconSizes.md, isLoading && 'animate-spin')} />
          </button>
        </div>
      </div>

      {isLoading ? (
        <div className="space-y-3">
          <Skeleton className="h-20" />
          <Skeleton className="h-20" />
          <Skeleton className="h-20" />
        </div>
      ) : isError ? (
        <Card>
          <CardContent className="pt-4">
            <div className="text-sm text-red-600">
              Error al cargar casos: {error instanceof Error ? error.message : 'desconocido'}
            </div>
          </CardContent>
        </Card>
      ) : !data || data.length === 0 ? (
        <Card>
          <CardContent className="pt-4">
            <div className="text-sm text-muted-foreground">No hay casos abiertos que coincidan.</div>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {data.map((row) => (
            <Card key={row.case.id}>
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-center gap-2">
                    <FileText className="h-4 w-4 text-muted-foreground" />
                    <div>
                      <div className="flex items-center gap-2">
                        <div className="font-semibold font-mono">{row.vehicle.registration}</div>
                        <Badge variant={row.case.status === 'OPEN' ? 'default' : 'secondary'} className={row.case.status === 'OPEN' ? 'bg-green-600' : ''}>
                          {row.case.status === 'OPEN' ? 'ABIERTO' : 'CERRADO'}
                        </Badge>
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {row.vehicle.make} {row.vehicle.model}{row.vehicle.year ? ` (${row.vehicle.year})` : ''} · {row.customer.name}
                      </div>
                    </div>
                  </div>

                  <Link
                    to={`/cases/${row.case.id}`}
                    className="text-sm text-blue-600 hover:text-blue-800 hover:underline inline-flex items-center gap-1"
                  >
                    Abrir
                    <ExternalLink className="h-3.5 w-3.5" />
                  </Link>
                </div>
              </CardHeader>
              <CardContent className="pt-0">
                <div className="text-xs text-muted-foreground">Actualizado: {formatTs(row.case.updatedAt)}</div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
